import React from 'react';
import useAsync from 'react-use/lib/useAsync';
import { isEmpty } from 'lodash';
import { Card, CardContent, CardHeader, Divider } from '@material-ui/core';
import {
  DependencyGraph,
  DependencyGraphTypes,
  Progress,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Structure,
  StructureModelInterface,
  useInventory,
} from '../../hooks/useInventory';

const namingMap: { [key: string]: string } = {
  application: 'Application',
  businesscapability: 'Business Capability',
  organizationstructure: 'Organization Structure',
  techcategory: 'Technology Category',
};

export const TeamInfoCard = () => {
  const { entity } = useEntity();

  const fetchLeanIXInventoryApi = useInventory();

  const { value, loading } = useAsync(async () => {
    return fetchLeanIXInventoryApi(entity);
  }, []);

  if (loading) {
    return <Progress />;
  }

  if (!value || Object.keys(value).length === 0) {
    return (
      <Card>
        <CardHeader title="LeanIX Team Structure" />
        <Divider />
        <CardContent>
          <p>No information available for this component.</p>
        </CardContent>
      </Card>
    );
  }

  const getShortName = (name: string, type?: string) => {
    if (!type) {
      return name.substring(name.lastIndexOf('/')).replace('/ ', '');
    }

    return `${type} | ${name
      .substring(name.lastIndexOf('/'))
      .replace('/ ', '')}`;
  };

  const extractNames = (
    structureModel?: StructureModelInterface,
  ): { id: string }[] => {
    const result = [];
    let node: StructureModelInterface | undefined = structureModel;

    while (node && !isEmpty(node)) {
      result.push({ id: getShortName(node.name, node.type) });
      node = node.parent;
    }

    const traverseChildren = (childNode?: StructureModelInterface) => {
      if (childNode && !isEmpty(childNode.children)) {
        for (const child of childNode?.children || []) {
          result.push({ id: getShortName(child.name, child.type) });
          traverseChildren(child);
        }
      }
    };

    traverseChildren(structureModel);
    return result;
  };

  const nodes = Object.keys(value).reduce((res, key: string) => {
    res[key] = extractNames(value[key as keyof Structure]);
    return res;
  }, {} as { [k: string]: { id: string }[] });

  const getEdges = (edge?: StructureModelInterface) => {
    const result: DependencyGraphTypes.DependencyEdge[] = [];
    let node = edge;

    while (node && !isEmpty(node) && node.parent) {
      result.push({
        from: getShortName(node.name, node.type),
        to: getShortName(node.parent.name, node.parent.type),
      });
      node = node.parent;
    }

    const traverseChildren = (childNode?: StructureModelInterface) => {
      if (childNode && !isEmpty(childNode.children)) {
        for (const child of childNode.children || []) {
          result.push({
            from: getShortName(childNode.name, childNode.type),
            to: getShortName(child.name, child.type),
          });
          traverseChildren(child);
        }
      }
    };

    traverseChildren(edge);
    return result;
  };

  const edges: { [key: string]: DependencyGraphTypes.DependencyEdge[] } =
    Object.keys(value).reduce((res, key) => {
      res[key] = getEdges(value[key as keyof Structure]);
      return res;
    }, {} as { [key: string]: DependencyGraphTypes.DependencyEdge[] });

  return (
    <>
      {Object.keys(nodes).map(
        category =>
          nodes[category] &&
          nodes[category].length > 0 && (
            <Card key={category}>
              <CardHeader title={`LeanIX ${namingMap[category]}`} />
              <Divider />
              <CardContent>
                <DependencyGraph
                  nodes={nodes[category]}
                  edges={edges[category]}
                />
              </CardContent>
            </Card>
          ),
      )}
    </>
  );
};
